import { getProperties } from '../services/properties'
import { getIssues } from '../services/issues'
import { getTenancies } from '../services/tenancies'
import { getProspects } from '../services/prospects'
import { getAllPendingInvites } from '../services/tenantInvites'
import type { SearchResult } from '../types/navigation'
import type { Property } from '../types/property'
import type { Issue } from '../types/maintenance'
import type { Tenancy } from '../types/tenancy'
import type { Prospect } from '../types/prospect'

export async function performGlobalSearch(query: string): Promise<SearchResult[]> {
  const [properties, issues, tenancies, prospects, pendingInvites] = await Promise.all([
    getProperties().catch(() => []),
    getIssues().catch(() => []),
    getTenancies().catch(() => []),
    getProspects().catch(() => []),
    getAllPendingInvites().catch(() => []),
  ])

  const lowerQuery = query.toLowerCase()
  const results: SearchResult[] = []

  // Search properties
  properties.forEach((p: Property) => {
    const searchText = `${p.name || ''} ${p.address_line1 || ''} ${p.city || ''} ${p.postcode || ''}`.toLowerCase()
    if (searchText.includes(lowerQuery)) {
      results.push({
        type: 'property',
        id: p.id,
        title: p.name || p.address_line1 || 'Unnamed Property',
        subtitle: `${p.city || ''} ${p.postcode || ''}`.trim() || 'Property'
      })
    }
  })

  // Search issues
  issues.forEach((i: Issue) => {
    const searchText = `${i.title || ''} ${i.reference_number || ''} ${i.description || ''}`.toLowerCase()
    if (searchText.includes(lowerQuery)) {
      const prop = i.property as { address_line1?: string } | null
      results.push({
        type: 'issue',
        id: i.id,
        title: i.title,
        subtitle: `${i.reference_number} • ${prop?.address_line1 || 'Unknown property'}`
      })
    }
  })

  // Search tenancies (rent, tenants, pending invites, property)
  tenancies.forEach((t: Tenancy) => {
    const activeTenants = t.tenants?.filter(tt => !tt.removed_at) || []
    const tenantNames = activeTenants.map(tt => tt.tenant?.full_name || '').join(' ')
    const tenantEmails = activeTenants.map(tt => tt.tenant?.email || '').join(' ')
    const tenantPhones = activeTenants.map(tt => tt.tenant?.phone || '').join(' ')

    // Include pending invites for this tenancy
    const invitesForTenancy = pendingInvites.filter(inv => inv.tenancy_id === t.id)
    const inviteText = invitesForTenancy
      .map(inv => `${inv.full_name} ${inv.email} ${inv.phone || ''}`)
      .join(' ')

    const prop = t.property as { name?: string; address_line1?: string } | null
    const propertyName = prop?.name || prop?.address_line1 || ''
    // Include common rent-related terms for searching
    const rentTerms = `rent due payment tenancy £${t.rent_amount}`
    const searchText = `${tenantNames} ${tenantEmails} ${tenantPhones} ${inviteText} ${propertyName} ${rentTerms}`.toLowerCase()
    if (searchText.includes(lowerQuery)) {
      const primaryTenant = activeTenants.find(tt => tt.is_primary)?.tenant?.full_name
        || activeTenants[0]?.tenant?.full_name
        || invitesForTenancy[0]?.full_name
        || 'No tenant'
      results.push({
        type: 'tenancy',
        id: t.id,
        title: primaryTenant,
        subtitle: `£${t.rent_amount}/${t.rent_frequency === 'monthly' ? 'mo' : 'wk'} • ${propertyName}`
      })
    }
  })

  // Search prospects
  prospects.forEach((p: Prospect) => {
    const searchText = `${p.full_name || ''} ${p.email || ''} ${p.phone || ''} prospect`.toLowerCase()
    if (searchText.includes(lowerQuery)) {
      results.push({
        type: 'prospect',
        id: p.id,
        title: p.full_name || 'Unnamed Prospect',
        subtitle: p.email || p.phone || 'Prospect'
      })
    }
  })

  return results.slice(0, 8) // Limit to 8 results
}
